import {
  AdminUsersApiError,
  safeAdminUsersErrorMessage,
  type AdminUsersClient,
  type ViewerAccountStatus,
  type ViewerCreationResult,
  type ViewerDirectoryItem,
  type ViewerSummary,
} from "./admin-users-api.ts";

export type ViewerFilter = "all" | ViewerAccountStatus | "delivery_failed";

export interface UserNotice {
  readonly message: string;
  readonly tone: "neutral" | "success" | "warning" | "error";
}

export const INITIAL_PASSWORD_MIN_LENGTH = 14;
export const INITIAL_PASSWORD_MAX_LENGTH = 128;

const INITIAL_PASSWORD_HAS_LOWERCASE = /[a-z]/;
const INITIAL_PASSWORD_HAS_UPPERCASE = /[A-Z]/;
const INITIAL_PASSWORD_HAS_NUMBER = /[0-9]/;
const INITIAL_PASSWORD_HAS_SYMBOL = /[^A-Za-z0-9]/;

export interface SensitiveInputValue {
  value: string;
}

export function canShowViewerProvisioning(
  canManageUsers: boolean,
  canProvisionViewers: boolean,
): boolean {
  return canManageUsers && canProvisionViewers;
}

export function initialPasswordMeetsPolicy(value: string): boolean {
  const length = Array.from(value).length;
  return (
    length >= INITIAL_PASSWORD_MIN_LENGTH &&
    length <= INITIAL_PASSWORD_MAX_LENGTH &&
    INITIAL_PASSWORD_HAS_LOWERCASE.test(value) &&
    INITIAL_PASSWORD_HAS_UPPERCASE.test(value) &&
    INITIAL_PASSWORD_HAS_NUMBER.test(value) &&
    INITIAL_PASSWORD_HAS_SYMBOL.test(value)
  );
}

export function clearSensitiveInput(input: SensitiveInputValue | null): void {
  if (input) input.value = "";
}

export function takeAndClearSensitiveInput(
  input: SensitiveInputValue | null,
): string {
  const value = input?.value ?? "";
  clearSensitiveInput(input);
  return value;
}

export const DIRECTORY_REFRESH_FAILED_GUIDANCE =
  "Reload this page to refresh the viewer directory before trying again.";

export type RetryableCreateDirectoryRefresh =
  | Readonly<{ kind: "not_needed" }>
  | Readonly<{
      kind: "refreshed";
      viewers: readonly ViewerDirectoryItem[];
    }>
  | Readonly<{ kind: "refresh_failed" }>;

export async function refreshViewersAfterRetryableCreateError(
  api: Pick<AdminUsersClient, "listViewers">,
  error: unknown,
): Promise<RetryableCreateDirectoryRefresh> {
  if (!(error instanceof AdminUsersApiError) || !error.retryable) {
    return Object.freeze({ kind: "not_needed" });
  }

  try {
    return Object.freeze({
      kind: "refreshed",
      viewers: await api.listViewers(),
    });
  } catch {
    return Object.freeze({ kind: "refresh_failed" });
  }
}

export function viewerCreateFailureNotice(
  error: unknown,
  reconciliation: RetryableCreateDirectoryRefresh,
): UserNotice {
  const originalMessage = safeAdminUsersErrorMessage(error);
  return Object.freeze({
    message:
      reconciliation.kind === "refresh_failed"
        ? `${originalMessage} ${DIRECTORY_REFRESH_FAILED_GUIDANCE}`
        : originalMessage,
    tone: "error",
  });
}

export function viewerSecondaryAction(
  viewer: ViewerSummary,
): "reset_access" | "send_setup" | "resend_setup" | null {
  if (viewer.status === "active") return "reset_access";
  if (viewer.status !== "pending") return null;
  return viewer.setupDeliveryStatus === "not_sent"
    ? "send_setup"
    : "resend_setup";
}

const CREATION_NOTICES: Readonly<
  Record<Exclude<ViewerCreationResult["outcome"], "recovered">, UserNotice>
> = Object.freeze({
  created: {
    message:
      "Invitation accepted for sending. The user chooses a password, signs in, and sets up an authenticator. Inbox delivery is not confirmed.",
    tone: "success",
  },
  pending_setup: {
    message:
      "Pending activation — an invitation was accepted for sending. Inbox delivery is not confirmed.",
    tone: "warning",
  },
  already_active: {
    message: "Already active — this viewer already has dashboard access.",
    tone: "neutral",
  },
});

export function viewerCreationNotice(result: ViewerCreationResult): UserNotice {
  if (result.viewer.status === "pending") {
    if (result.viewer.setupDeliveryStatus === "failed") {
      return {
        message: "Invitation sending failed. Retry from the user row.",
        tone: "error",
      };
    }
    if (result.viewer.setupDeliveryStatus === "not_sent") {
      return {
        message: "Invitation not sent. Use Send invitation to start setup.",
        tone: "warning",
      };
    }
  }
  if (result.outcome !== "recovered") {
    return CREATION_NOTICES[result.outcome];
  }
  return result.viewer.status === "active"
    ? {
        message: "Already active — existing viewer access was recovered.",
        tone: "success",
      }
    : {
        message:
          "Pending activation recovered — invitation accepted for sending. Inbox delivery is not confirmed.",
        tone: "success",
      };
}

export function viewerStatusPresentation(viewer: ViewerSummary): Readonly<{
  label:
    | "Active"
    | "Pending setup"
    | "Invitation not sent"
    | "Delivery failed"
    | "Disabled";
  tone: "active" | "pending" | "failed" | "disabled";
}> {
  if (viewer.status === "disabled") {
    return Object.freeze({ label: "Disabled", tone: "disabled" });
  }
  if (viewer.status === "active") {
    return Object.freeze({ label: "Active", tone: "active" });
  }
  return viewer.setupDeliveryStatus === "failed"
    ? Object.freeze({ label: "Delivery failed", tone: "failed" })
    : viewer.setupDeliveryStatus === "not_sent"
      ? Object.freeze({ label: "Invitation not sent", tone: "pending" })
      : Object.freeze({ label: "Pending setup", tone: "pending" });
}

export function matchesViewerFilter(
  viewer: ViewerDirectoryItem,
  filter: ViewerFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "delivery_failed") {
    return (
      viewer.status === "pending" && viewer.setupDeliveryStatus === "failed"
    );
  }
  return viewer.status === filter;
}

export interface ActionLock {
  current: string | null;
}

export function acquireAction(lock: ActionLock, actionId: string): boolean {
  if (lock.current !== null) return false;
  lock.current = actionId;
  return true;
}

export function releaseAction(lock: ActionLock, actionId: string): void {
  if (lock.current === actionId) lock.current = null;
}
